import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainContent } from '@/components/MainContent';
import { onAuthRequired } from '../utils/auth';
import { Sidebar } from './ui';
import { CreateCollectionModal } from './collections';
import { authenticatedFetch } from '@/utils/api';
import { AdaptiveUsageWidget, UsageWidgetErrorBoundary } from '@/components/UsageWidget';

interface Collection {
  id: number;
  name: string;
  is_private: boolean;
}

export const Dashboard: React.FC = () => {
  const { isLoggedIn } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [showMyPrompts, setShowMyPrompts] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Get usage widget variant from localStorage
  const [widgetVariant, setWidgetVariant] = useState<'badge' | 'footer' | 'floating' | 'icon'>(() => {
    const stored = localStorage.getItem('usage_widget_variant');
    return (stored as 'badge' | 'footer' | 'floating' | 'icon') || 'badge';
  });

  // Fetch collections when user is logged in
  const fetchCollections = async () => {
    if (!isLoggedIn) return;
    
    try {
      const response = await authenticatedFetch('/api/collections');
      
      if (response.ok) {
        const data = await response.json();
        setCollections(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      onAuthRequired();
    } else {
      fetchCollections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Listen for changes to widget variant preference
  useEffect(() => {
    const handleStorageChange = () => {
      const stored = localStorage.getItem('usage_widget_variant');
      const newVariant = (stored as 'badge' | 'footer' | 'floating' | 'icon') || 'badge';
      setWidgetVariant(newVariant);
    };

    // Listen for custom event (same window) and storage event (other windows/tabs)
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('widgetVariantChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('widgetVariantChanged', handleStorageChange);
    };
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleCreateCollection = (newCollection: any) => {
    setCollections(prev => [...prev, newCollection]);
    setShowCreateModal(false);
  };

  const handleSelectCollection = (collectionId: string | null) => {
    setSelectedCollectionId(collectionId);
    // Clear category selection and showMyPrompts when selecting a collection
    if (collectionId !== null) {
      setSelectedCategory(null);
      setShowMyPrompts(false);
    }
  };

  const handleSelectCategory = (category: string) => {
    setSelectedCategory(category);
    // Clear collection selection and showMyPrompts when selecting a category
    setSelectedCollectionId(null);
    setShowMyPrompts(false);
  };

  const handleSelectMyPrompts = () => {
    setShowMyPrompts(true);
    setSelectedCategory(null);
    setSelectedCollectionId(null);
  };

  if (!isLoggedIn) {
    return null; // or a loading spinner
  }

  return (
    <div className="flex flex-col w-full min-h-screen bg-gray-50 dark:bg-slate-950 transition-colors duration-300">
      
      <div className="flex flex-1 px-4 gap-8">
   
        <div className="hidden lg:block lg:w-1/5">
          <Sidebar
            isLoggedIn={isLoggedIn}
            isOpen={true}
            onClose={() => { }}
            collections={collections}
            selectedCollectionId={selectedCollectionId}
            onSelectCollection={handleSelectCollection}
            onShowCreateCollectionModal={() => setShowCreateModal(true)}
            selectedCategory={selectedCategory}
            onSelectCategory={handleSelectCategory}
            onSelectMyPrompts={() => handleSelectMyPrompts()}
            showMyPrompts={showMyPrompts}
          />
        </div>

      
        <div className="flex-1 flex flex-col">
          <div className='flex-1'>
            <MainContent 
              selectedCategory={selectedCategory} 
              setSelectedCategory={handleSelectCategory}
              selectedCollectionId={selectedCollectionId}
              showMyPrompts={showMyPrompts}
            />
          </div>
        </div>
      </div>

   
      {showCreateModal && (
        <CreateCollectionModal
          onClose={() => setShowCreateModal(false)}
          onCreateSuccess={handleCreateCollection}
        />
      )}

  
      {isLoggedIn && (widgetVariant === 'footer' || widgetVariant === 'floating') && (
        <UsageWidgetErrorBoundary>
          <AdaptiveUsageWidget variant={widgetVariant} />
        </UsageWidgetErrorBoundary>
      )}
    </div>
  );
};